import { CameraSource } from "@capacitor/camera";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { type CameraPermissionDeniedError } from "@src/appV2/lib/Media/CameraPermissionDeniedError";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { useBrowserOnFileChange } from "@src/appV2/lib/Media/useBrowserOnFileChange";
import { useMobileOnFileSelect } from "@src/appV2/lib/Media/useMobileOnFileSelect";
import { useTakePhoto } from "@src/appV2/lib/Media/useTakePhoto";
import { useCallback, useRef } from "react";

import { useNonInstantPayTimesheetUpload } from "./useNonInstantPayTimesheetUpload";

interface UseTimesheetFileUploadParams {
  shiftId: string;
  onUploadStart?: () => void;
  onUploadSuccess?: () => void;
  onUploadError?: (error: Error | CameraPermissionDeniedError) => void;
}

/**
 * Centralized hook for handling timesheet file upload business logic.
 * UI concerns like modals and navigation are handled via callbacks.
 */
export function useTimesheetFileUpload(props: UseTimesheetFileUploadParams) {
  const { shiftId, onUploadStart, onUploadSuccess, onUploadError } = props;

  const inputFileRef = useRef<HTMLInputElement>(null);

  const {
    uploadTimesheet,
    isLoading: isUploadingTimesheet,
    isError: isUploadError,
  } = useNonInstantPayTimesheetUpload({ shiftId });

  const handleUploadSuccess = useCallback(
    async (photo: UploadFileData) => {
      try {
        onUploadStart?.();
        await uploadTimesheet(photo.dataUrlBase64);
        onUploadSuccess?.();
      } catch (error) {
        const uploadError = error instanceof Error ? error : new Error("Upload failed");
        onUploadError?.(uploadError);
      }
    },
    [uploadTimesheet, onUploadStart, onUploadSuccess, onUploadError]
  );

  const handleUploadError = useCallback(
    (error: Error | CameraPermissionDeniedError) => {
      onUploadError?.(error);
    },
    [onUploadError]
  );

  const handleTakePhoto = useTakePhoto({
    source: CameraSource.Camera,
    onSuccess: handleUploadSuccess,
    onError: handleUploadError,
  });

  const handleSelectPhotoFromCameraRoll = useTakePhoto({
    source: CameraSource.Photos,
    onSuccess: handleUploadSuccess,
    onError: handleUploadError,
  });

  const handleSelectFile = useMobileOnFileSelect({
    onSuccess: handleUploadSuccess,
    onError: handleUploadError,
  });

  const browserSelectFile = useCallback(async () => {
    inputFileRef?.current?.click();
  }, []);

  const browserOnFileChange = useBrowserOnFileChange({
    onSuccess: handleUploadSuccess,
    onError: handleUploadError,
  });

  return {
    // Upload state
    isUploadingTimesheet,
    isUploadError,

    // File upload handlers
    fileUploaderHandlers: {
      onTakePhoto: isCapacitorPlatform() ? handleTakePhoto : handleSelectPhotoFromCameraRoll,
      onSelectPhotoFromCameraRoll: handleSelectPhotoFromCameraRoll,
      onSelectFile: isCapacitorPlatform() ? handleSelectFile : browserSelectFile,
    },

    // Browser file input
    inputFileRef,
    browserOnFileChange,
  };
}
